import { forwardRef, useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Clock, Loader2, Check, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const SLOTS = ["09:00 AM", "10:00 AM", "11:00 AM", "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM"];

function nextBusinessDays(count) {
  const days = [];
  const d = new Date();
  while (days.length < count) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) days.push(new Date(d));
  }
  return days;
}

export const BookMeeting = forwardRef((props, ref) => {
  const [dates] = useState(() => nextBusinessDays(10));
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", topic: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const iso = (d) => d.toISOString().slice(0, 10);
  const label = (d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) return toast.error("Pick a date and time");
    setLoading(true);
    try {
      await api.post("/meetings", { ...form, date: iso(date), time });
      setDone(true);
      toast.success("Demo booked! We'll send a confirmation shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white rounded-xl border border-[#D8E6F1] px-4 py-3.5 text-[#0C2A40] placeholder:text-[#93A5B5] focus:border-[#2880B8] focus:ring-2 focus:ring-[#2880B8]/20 focus:outline-none transition-all text-sm";

  return (
    <section id="book" ref={ref} data-testid="book-meeting" className="relative z-10 bg-white py-16 md:py-24 border-t border-[#D8E6F1]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
        <div>
          <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#2880B8] mb-5">/ Book a Demo</p>
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tighter text-[#0C2A40] leading-[0.9]">
            Pick a time that <span className="font-extrabold text-[#2880B8]">works for you.</span>
          </h2>
          <p className="mt-6 text-[#51667A] text-lg leading-relaxed max-w-md">
            A 30-minute walkthrough of the Astra platform tailored to your specialty and practice size. No pressure, no obligation.
          </p>
          <div className="mt-8 space-y-3 text-[#51667A] font-mono text-[11px] tracking-[0.12em] uppercase">
            <p className="flex items-center gap-2"><Clock size={15} className="text-[#2880B8]" /> 30 minutes - live screen-share</p>
            <p className="flex items-center gap-2"><CalendarCheck size={15} className="text-[#2880B8]" /> Instant confirmation</p>
            <p className="flex items-center gap-2"><Phone size={15} className="text-[#2880B8]" /> Phone follow-up available</p>
          </div>
        </div>

        {done ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="book-success" className="bg-[#F2F8FC] rounded-2xl border border-[#D8E6F1] p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-[#2880B8] flex items-center justify-center mx-auto"><Check size={26} className="text-white" /></div>
            <h3 className="mt-6 font-display text-2xl font-bold text-[#0C2A40]">You're booked!</h3>
            <p className="mt-3 text-[#51667A]">Demo scheduled for <b className="text-[#0C2A40]">{label(date)} at {time}</b>. A confirmation is on its way to {form.email}.</p>
          </motion.div>
        ) : (
          <motion.form initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} onSubmit={submit} className="bg-[#F2F8FC] rounded-2xl border border-[#D8E6F1] p-6 md:p-8 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input data-testid="book-name" required placeholder="Full name" value={form.name} onChange={update("name")} className={inputCls} />
              <input data-testid="book-email" required type="email" placeholder="Work email" value={form.email} onChange={update("email")} className={inputCls} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input data-testid="book-phone" placeholder="Phone" value={form.phone} onChange={update("phone")} className={inputCls} />
              <input data-testid="book-company" placeholder="Practice / company" value={form.company} onChange={update("company")} className={inputCls} />
            </div>

            <div>
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#93A5B5] mb-2">Select a date</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {dates.map((d) => {
                  const active = date && iso(d) === iso(date);
                  return (
                    <button key={iso(d)} type="button" data-testid={`book-date-${iso(d)}`} onClick={() => setDate(d)}
                      className={`shrink-0 px-3 py-2 rounded-xl border text-xs font-mono tracking-[0.05em] transition-colors ${active ? "bg-[#2880B8] text-white border-[#2880B8]" : "bg-white border-[#D8E6F1] text-[#0C2A40] hover:border-[#2880B8]"}`}>
                      {label(d)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#93A5B5] mb-2">Select a time</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {SLOTS.map((s) => (
                  <button key={s} type="button" data-testid={`book-time-${s.replace(/[ :]/g, "")}`} onClick={() => setTime(s)}
                    className={`px-2 py-2.5 rounded-xl border text-xs font-mono transition-colors ${time === s ? "bg-[#2880B8] text-white border-[#2880B8]" : "bg-white border-[#D8E6F1] text-[#0C2A40] hover:border-[#2880B8]"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <button data-testid="book-submit" type="submit" disabled={loading} className="w-full py-4 rounded-full bg-[#2880B8] text-white font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-[#1F6EA1] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />} Confirm Demo
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
});

BookMeeting.displayName = "BookMeeting";
